import React from 'react';
import { Tag, Tooltip } from 'antd';
import {
  CheckCircleOutlined, AlertOutlined, CloseCircleOutlined, WarningOutlined,
  SyncOutlined, PauseCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';

import { useTranslation } from 'react-i18next';

/**
 * TagRc - badge réutilisable représentant l'état de négociation RC d'un adversaire.
 *
 * Props :
 *  - positionRc            : 'EN_ATTENTE' | 'EN_NEGOCIATION' | 'REJETEE' | 'TRANCHEE'
 *  - pourcentageRcFinal    : number (utile seulement pour TRANCHEE)
 *  - pourcentageRcPropose  : number (utile pour EN_NEGOCIATION)
 *  - nombreToursRc         : number (T1, T2…)
 *  - dateDerniereActionRc  : ISO string - pour calcul d'ancienneté (alertes)
 *  - size                  : 'sm' | 'md' (défaut md)
 */
export default function TagRc({
  positionRc,
  pourcentageRcFinal,
  pourcentageRcPropose,
  nombreToursRc,
  dateDerniereActionRc,
  size = 'md',
}) {
  const { t } = useTranslation('commun');
  const s = computeState({ positionRc, pourcentageRcFinal, pourcentageRcPropose, nombreToursRc, dateDerniereActionRc }, t);
  const padding = size === 'sm' ? '1px 8px' : '2px 10px';
  const fontSize = size === 'sm' ? 10.5 : 11.5;

  const tag = (
    <Tag
      color={s.color}
      style={{
        borderRadius: 20,
        padding,
        fontSize,
        fontWeight: 600,
        border: s.border,
        color: s.textColor,
        background: s.background,
        margin: 0,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
      }}
    >
      <span>{s.icon}</span>
      <span>{s.label}</span>
    </Tag>
  );

  return s.tooltip ? <Tooltip title={s.tooltip}>{tag}</Tooltip> : tag;
}

/* Calcule l'apparence + tooltip selon l'état. Retourne { icon, label, color, textColor, background, border, tooltip } */
function computeState({ positionRc, pourcentageRcFinal, pourcentageRcPropose, nombreToursRc, dateDerniereActionRc }, t) {
  const joursDepuis = dateDerniereActionRc ? dayjs().diff(dayjs(dateDerniereActionRc), 'day') : null;
  const tours = nombreToursRc ?? 0;

  switch (positionRc) {
    case 'TRANCHEE':
      return {
        icon: <CheckCircleOutlined />,
        label: `${pourcentageRcFinal ?? 0}%`,
        color: 'success',
        textColor: '#15803d',
        background: '#ecfdf5',
        border: '1px solid #bbf7d0',
        tooltip: t('tagRc.trancheeVerrouillee', { taux: pourcentageRcFinal }),
      };

    case 'EN_NEGOCIATION': {
      const urgent = joursDepuis != null && joursDepuis > 15;
      return {
        icon: urgent ? <AlertOutlined /> : <SyncOutlined />,
        label: urgent
          ? t('tagRc.sansReponse', { tours })
          : t('tagRc.enNego', { tours }),
        color: urgent ? 'error' : 'warning',
        textColor: urgent ? '#b42318' : '#b45309',
        background: urgent ? '#fef2f2' : '#fff7ed',
        border: `1px solid ${urgent ? '#fecaca' : '#fed7aa'}`,
        tooltip: pourcentageRcPropose != null
          ? (joursDepuis != null ? t('tagRc.propositionJours', { taux: pourcentageRcPropose, tours, jours: joursDepuis }) : t('tagRc.proposition', { taux: pourcentageRcPropose, tours }))
          : t('tagRc.enNegociation', { tours }),
      };
    }

    case 'REJETEE': {
      const urgent = joursDepuis != null && joursDepuis > 10;
      return {
        icon: urgent ? <AlertOutlined /> : <CloseCircleOutlined />,
        label: urgent
          ? t('tagRc.rejeteeActionRequise', { tours })
          : t('tagRc.rejetee', { tours }),
        color: 'error',
        textColor: '#b42318',
        background: urgent ? '#fee2e2' : '#fef2f2',
        border: `1px solid ${urgent ? '#fca5a5' : '#fecaca'}`,
        tooltip: urgent
          ? t('tagRc.rejetNonTraite', { jours: joursDepuis })
          : t('tagRc.compagnieARejete'),
      };
    }

    case 'EN_ATTENTE':
    default: {
      const urgent = joursDepuis != null && joursDepuis > 7;
      return {
        icon: urgent ? <WarningOutlined /> : <PauseCircleOutlined />,
        label: urgent ? t('tagRc.rcATraiter') : t('tagRc.rcEnAttente'),
        color: urgent ? 'warning' : 'default',
        textColor: urgent ? '#b45309' : '#475569',
        background: urgent ? '#fff7ed' : '#f1f5f9',
        border: `1px solid ${urgent ? '#fed7aa' : '#e2e8f0'}`,
        tooltip: urgent
          ? t('tagRc.aucunePropositionDepuis', { jours: joursDepuis })
          : t('tagRc.aucunePropositionFaite'),
      };
    }
  }
}

/**
 * Helper : agrège la position RC d'un sinistre depuis sa liste d'adversaires.
 * Retourne { positionAgregee, nbTotal, nbTranchees }.
 */
export function aggregateRc(adversaires = []) {
  const advs = adversaires.filter(a => a.estAdversaire);
  if (advs.length === 0) return { positionAgregee: null, nbTotal: 0, nbTranchees: 0 };

  const nbTranchees = advs.filter(a => a.positionRc === 'TRANCHEE').length;
  if (nbTranchees === advs.length) {
    return { positionAgregee: 'TRANCHEE', nbTotal: advs.length, nbTranchees };
  }
  if (advs.some(a => a.positionRc === 'REJETEE')) {
    return { positionAgregee: 'REJETEE', nbTotal: advs.length, nbTranchees };
  }
  if (advs.some(a => a.positionRc === 'EN_NEGOCIATION')) {
    return { positionAgregee: 'EN_NEGOCIATION', nbTotal: advs.length, nbTranchees };
  }
  return { positionAgregee: 'EN_ATTENTE', nbTotal: advs.length, nbTranchees };
}
